// Globals
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga';

// UI
import {
  CaretDownOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Dropdown,
  Flex,
  Layout as AntdLayout,
  MenuProps,
  Pagination,
  Popover,
  Row,
} from 'antd';

// Locals
import { DEFAULT_SORT_TYPE, ITEMS_PER_PAGE } from '../components/utils';
import useGiideList from '../hooks/useQuery/useGiideList';
import useGiideSubscriptions from '../hooks/useSubscription/useGiideSubscriptions';
import styles from './Pages.module.css';
import {
  setCreateGiideError,
  setGiide,
  setGiideFetching,
  setOpenedStep,
  setOwnRecordingStarted,
  setUpgradeModalOpen,
} from '../actions';

// import dashboardWelcome from '../../src/studio/assets/dashboard/dashboardWelcome.png';
import dashboardWelcome from '../../src/studio/assets/newBrandingIllustrations/giide-illo-01.jpg';

import GiideListTable from '../components/GiideList/GiideListTable';
import useGiideListPagination from '../hooks/useGiideListPagination';
import GiideListGrid from '../components/GiideList/GiideListGrid';
import FilterBox from '../components/GiideList/FilterBox';
import OnboardingModal from '../components/OnboardingModal';
import useUser from '../hooks/useUser';
import CheatSheetModal from '../components/GiideList/CheatSheetModal';
import GiidePlayerModal from '../components/GiideList/GiidePlayerModal';
import { IState } from '../graphql/types';
import useGiideOperations from '../hooks/useGiideOperations';

import useUserPreference from '../hooks/useUserPreference';

import step1 from '../studio/assets/newBrandingIllustrations/giide-audio.jpg';
import step2 from '../studio/assets/newBrandingIllustrations/giide-create.jpg';
import step3 from '../studio/assets/newBrandingIllustrations/giide-publish.jpg';

import AccountDisabledBanner from '../components/Account/AccountDisabledBanner';
import AccountDisabledPopover from '../components/Account/AccountDisabledPopover';
import CreateGiideError from '../components/GiideList/CreateGiideError';
import { AccountStatus } from '../enums';
import { CreateGiideErrorType } from '../types';
import { getIsGiideLimitedReached, getTotalGiidesCreated } from './utils';
import { colors } from '../theme';
import DuplicateGiideModal from '../components/Duplication/DuplicateGiideModal';

const CreateGiideErrorPopover = () => <CreateGiideError />;

const DisabledPopover = () => (
  <AccountDisabledPopover
    explanation={'Upgrade your plan to create a new giide.'}
  />
);

const giides = [
  {
    title: 'Start here my friend, your first giide',
    author: 'Allison Kent-Smith',
    duration: '0:58',
    giideId: '4116f534-b57d-4061-bdf2-81cf957e98d7',
    thumbnail: step1,
    altText: 'step 1',
  },
  {
    title: 'Next, Add Some Links, Images, and Videos',
    author: 'Allison Kent-Smith',
    duration: '0:54',
    giideId: 'aebd74e0-f8bc-45b8-92bf-18a3d2a100e9',
    thumbnail: step2,
    altText: 'step 2',
  },
  {
    title: 'Publish and share or embed on the web',
    author: 'Allison Kent-Smith',
    duration: '0:49',
    giideId: '606bde3e-2664-4267-be8b-5dfa906533f2',
    thumbnail: step3,
    altText: 'step 3',
  },
];

const GiideList: React.FC = () => {
  const dispatch = useDispatch();
  useGiideSubscriptions();
  const {
    refetch: refetchGiideListData,
    filteredData,
    giideListSortType,
    handleSortChange,
    giideListViewType,
    giideListFilter,
    giideCount,
  } = useGiideList(true);
  const { paginationPage, handlePageChange, paginationButtonsRender } =
    useGiideListPagination();
  const { preference: hasShowedFirstLoginContent } =
    useUserPreference('firstLoginVideo');
  useState<boolean>(false);
  const { user } = useUser();

  const [cheatSheetModalVisible, setCheatSheetModalVisible] =
    useState<boolean>(false);

  const [giidePlayerModal, setGiidePlayerModal] = useState<{
    visible: boolean;
    giideId: string;
  }>({
    visible: false,
    giideId: giides[0].giideId,
  });

  const accountDisabled = useSelector<IState, boolean>(
    state => state.accountDisabled
  );

  const giideListFetching = useSelector<IState, boolean>(
    state => !!state.giideListFetching
  );
  const { createNewGiide, giideSaving } = useGiideOperations();
  const ownRecordingStarted = useSelector<IState, boolean | undefined>(
    state => state.ownRecordingStarted
  );

  useEffect(() => {
    batch(() => {
      dispatch(setOpenedStep(undefined));
      dispatch(setGiide(null));
    });
  }, [dispatch]);

  useEffect(() => {
    if (user?.plan?.accountStatus === 'free_trial_expired') {
      setGiidePlayerModal({ ...giidePlayerModal, visible: false });
      setCheatSheetModalVisible(false);
    }
  }, [user?.plan?.accountStatus, giidePlayerModal]);

  useEffect(() => {
    if (ownRecordingStarted) dispatch(setOwnRecordingStarted(undefined));
  }, [dispatch, ownRecordingStarted]);

  useEffect(() => {
    dispatch(setGiideFetching(true));
    refetchGiideListData();
  }, [dispatch, user, refetchGiideListData]);

  const sortByMenuItems: MenuProps['items'] = useMemo(() => {
    const sortTypes = ['Last Edited', 'Title', 'Author', 'Status'];

    return sortTypes.map((item, index) => {
      let showAsDesc: boolean | null = null;

      if (giideListSortType?.type === item) {
        showAsDesc = !giideListSortType?.descending;
        if (giideListSortType?.type === 'Title') {
          showAsDesc = !showAsDesc;
        }
      } else if (giideListSortType?.type === 'Edited by' && index === 0) {
        showAsDesc = true;
      }

      return {
        key: item,
        label: (
          <div
            className={styles.dropdownMenuWithSortIcon}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '120px',
              justifyContent: 'space-between',
            }}
          >
            <div>{item}</div>

            {showAsDesc !== null &&
              (showAsDesc ? (
                <SortDescendingOutlined />
              ) : (
                <SortAscendingOutlined />
              ))}
          </div>
        ),
      };
    });
  }, [giideListSortType]);

  const onClickSortMenuItem: MenuProps['onClick'] = ({ key }) => {
    handleSortChange(key);
  };

  useEffect(() => {
    if (
      giideListViewType === 'grid' &&
      giideListSortType?.type === 'Visibility'
    ) {
      handleSortChange(DEFAULT_SORT_TYPE);
    }
  }, [giideListViewType, giideListSortType, handleSortChange]);

  const handleCreateGiide = useCallback(async () => {
    await createNewGiide();
  }, [createNewGiide]);

  const totalGiidesCreated = getTotalGiidesCreated(user);
  const giideLimitReached = getIsGiideLimitedReached(user, totalGiidesCreated);

  useEffect(() => {
    if (!giideLimitReached) {
      dispatch(setCreateGiideError(null));
    } else {
      dispatch(setCreateGiideError(CreateGiideErrorType.maximumReached));
    }
  }, [giideLimitReached, dispatch]);

  if (!user) {
    return null;
  }

  const onCreateGiide = async () => {
    if (accountDisabled) {
      return;
    }

    if (giideLimitReached) {
      dispatch(setCreateGiideError(CreateGiideErrorType.maximumReached));
      return;
    }

    ReactGA.event({
      category: 'Button',
      action: 'Create New Giide Pressed',
    });

    await handleCreateGiide();
  };

  return (
    <>
      {user &&
        hasShowedFirstLoginContent !== undefined &&
        !hasShowedFirstLoginContent && <OnboardingModal />}

      <CheatSheetModal
        showModal={cheatSheetModalVisible}
        setShowModal={setCheatSheetModalVisible}
      />

      <GiidePlayerModal
        showModal={giidePlayerModal.visible}
        setShowModal={setGiidePlayerModal}
        giideId={giidePlayerModal.giideId}
      />

      <DuplicateGiideModal />

      <AntdLayout.Content className="main-content">
        <Flex style={{ width: '960px', margin: 'auto' }} vertical={true}>
          {accountDisabled && (
            <AccountDisabledBanner
              accountWasCanceled={
                user?.plan.accountStatus === AccountStatus.paddleCanceled ||
                user?.plan.accountStatus === AccountStatus.paddleDeleted
              }
            />
          )}

          <div
            className={`${styles.dashboardTopRow} aa ${
              giideListViewType === 'list' ? styles.dashboardTopRowList : ''
            }`}
            style={{ marginBottom: '24px' }}
          >
            <Flex
              gap={'middle'}
              align={'center'}
              className={styles.myDashboardWrapper}
            >
              My Dashboard
            </Flex>
            <div className={styles.filterBoxWrapper}>
              {giideListFilter &&
                Object.keys(giideListFilter).map(key => (
                  <div key={key}>
                    {giideListFilter[key] && <FilterBox key={key} text={key} />}
                  </div>
                ))}
            </div>
            <div className={styles.dashboardRightCol}>
              <Popover
                placement={'bottom'}
                content={
                  accountDisabled
                    ? DisabledPopover
                    : giideLimitReached
                    ? CreateGiideErrorPopover
                    : null
                }
              >
                <Button
                  type={'primary'}
                  onClick={onCreateGiide}
                  loading={giideSaving}
                  disabled={giideSaving || accountDisabled || giideLimitReached}
                  style={{ marginRight: '2px' }}
                >
                  Create New Giide
                </Button>
              </Popover>

              <CreateGiideError
                noShowMaximumReached
                style={{ position: 'absolute', top: 48 }}
              />
            </div>
          </div>

          <Flex
            justify={'space-between'}
            style={{ marginBottom: '2px' }}
            align={'center'}
            gap={'large'}
            className={styles.dashboardTopRow}
          >
            {giideListFetching ? (
              <Flex gap={'small'} align={'center'} style={{ height: '32px' }}>
                <SyncOutlined
                  style={{ color: colors.black2, fontSize: '16px' }}
                  spin
                />
                <div style={{ fontSize: '14px', color: colors.black2 }}>
                  Refreshing...
                </div>
              </Flex>
            ) : (
              <Flex justify={'flex-start'} gap={'middle'} align={'center'}>
                {giideListViewType === 'grid' && giideCount > 0 && (
                  <>
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: sortByMenuItems,
                        onClick: onClickSortMenuItem,
                      }}
                    >
                      <Button
                        type={'text'}
                        className={styles.sortByButton}
                        onClick={e => e.preventDefault()}
                      >
                        Sort By: {giideListSortType?.type}
                        <CaretDownOutlined />
                      </Button>
                    </Dropdown>
                  </>
                )}

                {user?.plan &&
                  user.plan.features?.giideMaxNumberOfGiides?.hasLimit &&
                  giideCount !== 0 && (
                    <div
                      style={{
                        color: giideLimitReached ? colors.red4 : colors.black3,
                        fontSize: '12px',
                      }}
                    >
                      Total Giides: {totalGiidesCreated} /{' '}
                      {user.plan.features.giideMaxNumberOfGiides.limitAmount}
                    </div>
                  )}

                {giideLimitReached && (
                  <Button
                    type={'default'}
                    size={'small'}
                    onClick={() => dispatch(setUpgradeModalOpen(true))}
                  >
                    Upgrade Now
                  </Button>
                )}
              </Flex>
            )}
          </Flex>

          <Row>
            {giideCount === 0 && !giideListFetching && !filteredData.length ? (
              <Col
                className={`${styles.giideCardCol} ${styles.noGiideWrapper}`}
              >
                <div>
                  <img
                    className={styles.dashboardWelcome}
                    src={dashboardWelcome}
                    alt="dashboard welcome"
                  />
                  <div>
                    <div className={styles.welcomeHeader}>Welcome</div>
                    <div className={styles.welcomeText}>
                      When you create a giide, it will appear here.
                    </div>
                  </div>
                  <div className={styles.giideSection}>
                    {giides.map(giide => (
                      <div
                        className={styles.introGiideCard}
                        key={`intro-giide-card-${giide.giideId}`}
                        onClick={() =>
                          setGiidePlayerModal({
                            visible: true,
                            giideId: giide.giideId,
                          })
                        }
                      >
                        <img src={giide.thumbnail} alt={giide.altText} />
                        <span>{giide.duration}</span>
                        <h4>{giide.title}</h4>
                        <p>{giide.author}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            ) : giideListViewType === 'grid' ? (
              <GiideListGrid />
            ) : (
              <GiideListTable />
            )}
          </Row>
          {giideListViewType === 'grid' && (
            <Row align="middle" justify="center">
              <Col>
                <Row>
                  <Pagination
                    defaultCurrent={paginationPage?.page ?? 1}
                    defaultPageSize={ITEMS_PER_PAGE}
                    onChange={handlePageChange}
                    total={filteredData ? filteredData.length : 0}
                    showSizeChanger={false}
                    itemRender={paginationButtonsRender}
                    className={`paginationBar ${styles.paginationBar}`}
                    hideOnSinglePage={true}
                  />
                </Row>
              </Col>
            </Row>
          )}
        </Flex>
      </AntdLayout.Content>
    </>
  );
};

export default GiideList;
